import React, { useState } from 'react';
import type { DealCompletionOverride } from '../data/api';

/**
 * The way past the completion gate: ask a superuser to close the deal without
 * the requirements that are still outstanding.
 *
 * One panel, four states, always telling the operator what happens next:
 *   - nothing live and the gate is failing  -> "Request override" with a reason
 *   - PENDING                                -> who was asked, when, for what
 *   - APPROVED and unused                    -> the deal can be closed now
 *   - DECLINED (latest)                      -> the note, and a way to ask again
 * Only the requirements open at request time are waived; the panel says so
 * because that is the surprise an operator would otherwise hit at close.
 */
export const DealCompletionOverridePanel: React.FC<{
  override: DealCompletionOverride | null;
  canComplete: boolean;
  isCompleted: boolean;
  isCancelled: boolean;
  onRequest: (reason: string) => Promise<void>;
}> = ({ override, canComplete, isCompleted, isCancelled, onRequest }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (isCancelled) return null;

  const live = override && !override.appliedAt && (override.status === 'PENDING' || override.status === 'APPROVED')
    ? override
    : null;

  const fmtWhen = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reason.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      await onRequest(reason.trim());
      setReason('');
      setOpen(false);
    } catch (err: any) {
      setError(err?.message || 'The override request could not be sent.');
    } finally {
      setSaving(false);
    }
  };

  const waivedList = (rows: DealCompletionOverride['waivedRequirements']) => (
    <ul className="mt-2 grid gap-1 text-sm text-avcs-text">
      {rows.map((r) => (
        <li key={`${r.party}-${r.code}`} className="flex items-baseline gap-2">
          <span aria-hidden className="text-[#b06a00]">⌀</span>
          <span>
            {r.label}
            {r.party && <span className="text-avcs-muted"> ({r.party.toLowerCase()})</span>}
          </span>
        </li>
      ))}
    </ul>
  );

  if (isCompleted && override?.appliedAt) {
    return (
      <div className="rounded-xl border border-[#1e6bb833] bg-[#f6faff] p-4">
        <div className="text-sm font-semibold text-avcs-text">Closed under override</div>
        <p className="mt-1 text-sm text-avcs-muted">
          Approved by {override.decidedByName || 'a superuser'} on {fmtWhen(override.decidedAt)}; closed {fmtWhen(override.appliedAt)}.
          {override.decisionNote ? ` Note: “${override.decisionNote}”` : ''}
        </p>
        <div className="mt-2 text-xs uppercase tracking-wide text-avcs-muted">Requirements waived</div>
        {waivedList(override.waivedRequirements)}
      </div>
    );
  }

  if (live?.status === 'APPROVED') {
    return (
      <div className="rounded-xl border border-[#b9e7c9] bg-[#f0faf4] p-4" role="status">
        <div className="text-sm font-semibold text-[#106b38]">Override approved — this deal can be closed now</div>
        <p className="mt-1 text-sm text-avcs-muted">
          {live.decidedByName || 'A superuser'} approved on {fmtWhen(live.decidedAt)}.
          {live.decisionNote ? ` Note: “${live.decisionNote}”` : ''} Only the requirements below are waived; anything
          new the deal needs will still hold it open.
        </p>
        {waivedList(live.waivedRequirements)}
      </div>
    );
  }

  if (live?.status === 'PENDING') {
    return (
      <div className="rounded-xl border border-[#ecd1b9] bg-[#fff7ef] p-4" role="status">
        <div className="text-sm font-semibold text-[#7b4a18]">Override requested — awaiting a superuser's decision</div>
        <p className="mt-1 text-sm text-avcs-muted">
          {live.requestedByName || 'An administrator'} asked on {fmtWhen(live.requestedAt)}
          {live.notifiedEmails.length ? `; ${live.notifiedEmails.join(', ')} ${live.notifiedEmails.length === 1 ? 'was' : 'were'} emailed a review link.` : '.'}
        </p>
        <p className="mt-2 text-sm text-avcs-text">“{live.reason}”</p>
        <div className="mt-2 text-xs uppercase tracking-wide text-avcs-muted">Would waive</div>
        {waivedList(live.waivedRequirements)}
      </div>
    );
  }

  if (canComplete || isCompleted) return null;

  const declined = override && override.status === 'DECLINED' ? override : null;

  return (
    <div className="rounded-xl border border-[#1e6bb824] bg-white p-4">
      {declined && (
        <div className="mb-3 rounded-lg border border-[#e7bbb9] bg-[#fdf1ef] px-3 py-2 text-sm text-[#6b1010]" role="status">
          The last request was declined by {declined.decidedByName || 'a superuser'} on {fmtWhen(declined.decidedAt)}
          {declined.decisionNote ? `: “${declined.decisionNote}”` : '.'}
        </div>
      )}
      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-avcs-muted">
            If the outstanding requirements will never be met, a superuser can approve closing the deal without them.
          </p>
          <button type="button" className="btn" onClick={() => setOpen(true)}>
            {declined ? 'Request override again' : 'Request override'}
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="grid gap-3" aria-label="Request a close-out override">
          <label className="grid gap-1 text-sm font-medium text-avcs-text">
            Why should this deal close without the outstanding requirements?
            <textarea
              className="min-h-24 rounded-lg border border-[#1e6bb824] bg-white px-3 py-2 text-sm font-normal text-avcs-text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Purchaser paid in full on 12 Aug and has confirmed by phone that no bank letter will be issued."
              autoFocus
            />
          </label>
          <p className="text-xs text-avcs-muted">
            Every superuser with an email address gets a review link. Approval waives only what is outstanding right now.
          </p>
          {error && <div role="alert" className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="btn" onClick={() => { setOpen(false); setError(''); }} disabled={saving}>Cancel</button>
            <button type="submit" className="btn primary" disabled={!reason.trim() || saving} aria-busy={saving}>
              {saving ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DealCompletionOverridePanel;
